import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { MatRadioModule } from '@angular/material/radio';
import { BadgeComponent, UiRadioWrappedDirective } from '@ui';

interface CleaningOption {
  value: string;
  label: string;
  description: string;
  recommended?: boolean;
  disabled?: boolean;
}

@Component({
  selector: 'sbnb-add-task-rule-flow-cleaning-auto-manual',
  standalone: true,
  imports: [CommonModule, MatRadioModule, UiRadioWrappedDirective, BadgeComponent],
  templateUrl: './add-task-rule-flow-cleaning-auto-manual.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddTaskRuleFlowCleaningAutoManualComponent {
  hasAtLeastOneEligibleAutomatedCleaningProperty = input<boolean>();
  isOnLegacyPlan = input<boolean>(false);
  selectedOption = signal<'automatic' | 'manual' | null>(null);
  optionSelected = output<'automatic' | 'manual'>();

  readonly cleaningOptions = computed(() => {
    const hasEligibleProperty = this.hasAtLeastOneEligibleAutomatedCleaningProperty();
    const isLegacy = this.isOnLegacyPlan();

    const options: CleaningOption[] = [
      {
        value: 'automatic',
        label: 'Automatic',
        description: this.#getAutomaticDescription(isLegacy, hasEligibleProperty),
        recommended: !isLegacy && hasEligibleProperty !== false,
        disabled: isLegacy || hasEligibleProperty === false,
      },
      {
        value: 'manual',
        label: 'Manual',
        description: 'We automate scheduling and communication, but you need to find cleaners and pay them manually.',
      },
    ];

    return options;
  });

  #getAutomaticDescription(isLegacy: boolean, hasEligibleProperty: boolean | undefined): string {
    if (isLegacy) {
      return 'Not available on legacy plans. Please upgrade to access automated cleaning services.';
    }
    if (hasEligibleProperty === false) {
      return 'Not available for your properties. Please contact support for more information.';
    }
    return 'We find and schedule cleaners, message them, and pay them. You set the checklist.';
  }

  onOptionChange(value: 'automatic' | 'manual'): void {
    this.selectedOption.set(value);
    this.optionSelected.emit(value);
  }

  onOptionClick(value: string, disabled?: boolean): void {
    if (!disabled && (value === 'automatic' || value === 'manual')) {
      this.onOptionChange(value);
    }
  }
}
